import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

import { namedInput } from "./timesheet_voice_form"
import { changedFieldNames, fieldValues } from "./timesheet_voice_snapshot"
import { VoiceLevelMeter } from "./voice_level_meter"
import { VoiceQuestionQueue } from "./voice_question_queue"

// The voice input card every voice feature shares (Voice::CardComponent). A small
// state machine; the card shows each state through its data-state:
//
//   idle → arming (microphone starting) → recording → processing (upload +
//   transcription) → finalizing (transcript shown, fields being worked out) →
//   applied (summary, correction, undo); error from any working state.
//
// Two requests: transcribe returns the transcript (shown at once) and a token,
// extract turns the token into suggestions. The feature's controller extends
// this class and supplies the form-specific parts:
//
//   extractRequest(token) -> { body, correction }   what the extract step sends
//   takeSnapshot() / restoreSnapshot(snapshot)       what "Rückgängig" puts back
//   applyPayload(payload) -> { applied, missing }    writes the suggestions
//   renderSummary(changed)                           the applied state's details
//   label(key)                                       a field's name for hints
//
// Every field a pass changed gets a "Sprache" tag until the user edits it.
//
// Rückfragen (question state, between applied passes): what a pass left unclear
// (payload.questions) and — with a questions URL — the required fields the form
// still leaves empty, asked one at a time (VoiceQuestionQueue). A tapped option
// is written by the feature; a spoken answer is a correction pass that sends the
// question along, so the server changes only its field. The feature adds:
//
//   questionsRequest() -> body                       what the required-field check sends
//   applyAnswer(question, value) -> applied count    writes a tapped option
//   answerFieldName(question) -> input name          the field a question is about
//
// "Rückgängig" puts the form back as it was before the session's last spoken
// pass that was not an answer — answers included.
const RESTING = ["idle", "applied", "question"]
const WORKING = ["arming", "processing", "finalizing"]
const TAG_CLASSES = ["ring-2", "ring-emerald-300", "ring-offset-1"]
const BADGE_CLASSES =
  "ml-1.5 inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-px text-[11px] font-semibold text-emerald-800"
const HIGHLIGHT_CLASSES = "rounded bg-emerald-100 px-1 py-px text-emerald-900"
// Said but filled into nothing (the payload's unmatched_highlights).
const UNMATCHED_CLASSES = "rounded bg-amber-100 px-1 py-px text-amber-900"

export class VoiceCardController extends Controller {
  static targets = [
    "mic",
    "idleSubtitle",
    "appliedTitle",
    "appliedSubtitle",
    "errorMessage",
    "timer",
    "detailsToggle",
    "examples",
    "meter",
    "progress",
    "step",
    "transcript",
    "summary",
    "hints",
    "hintList",
    "chipTemplate",
    "rowTemplate",
    "undoButton",
    "questionCounter",
    "questionDetail",
    "questionPrompt",
    "questionOptions",
    "questionOptionTemplate",
    "questionOther",
    "questionType",
    "questionRemaining",
    "answeredList",
  ]
  static values = {
    transcribeUrl: String,
    extractUrl: String,
    // The rule-based required-field check (voice_questions); none: no such questions.
    questionsUrl: String,
    maxSeconds: { type: Number, default: 180 },
    // key => label, for naming values in the summary and the "nicht übernommen" hint
    labels: { type: Object, default: {} },
    // i18n texts written at runtime (see Voice::CardComponent#messages)
    messages: { type: Object, default: {} },
  }

  connect() {
    this.hasAppliedOnce = false
    this.restingState = "idle"
    this.tags = []
    this.hintCount = 0
    this.queue = new VoiceQuestionQueue()
    this.transcripts = []
    this.answering = null
    this.sessionApplied = 0
    this.idleText = this.hasIdleSubtitleTarget ? this.idleSubtitleTarget.textContent : ""
    // Only the user's own edits remove a "Sprache" tag; the writer's events are synthetic.
    this.onUserEdit = (event) => {
      if (event.isTrusted) this.untag(event.target)
    }
    this.form()?.addEventListener("input", this.onUserEdit)
    this.form()?.addEventListener("change", this.onUserEdit)
  }

  disconnect() {
    this.form()?.removeEventListener("input", this.onUserEdit)
    this.form()?.removeEventListener("change", this.onUserEdit)
    this.stopRecordingResources()
  }

  // --- Actions ---

  toggle() {
    const state = this.element.dataset.state
    if (state === "recording") {
      this.finish()
    } else if (state === "question") {
      this.speakAnswer()
    } else if (!WORKING.includes(state)) {
      this.start()
    }
  }

  retry() {
    this.start()
  }

  // --- Rückfragen ---

  speakAnswer() {
    if (this.advancing) return
    const question = this.queue.current()
    if (!question) return
    this.setAnswering(question)
    this.start()
  }

  answerOption(event) {
    if (this.advancing) return
    const question = this.queue.current()
    const option = question?.options?.[Number(event.currentTarget.dataset.index)]
    if (!option) return
    if (!this.undoState)
      this.undoState = { snapshot: this.takeSnapshot(), hadApplied: this.hasAppliedOnce }
    const applied = this.applyAnswer(question, option.value)
    const name = this.answerFieldName(question)
    if (name) this.tag(name)
    this.sessionApplied += applied
    this.queue.answer(question, option.label)
    this.nextQuestion()
  }

  skipQuestion() {
    if (this.advancing) return
    const question = this.queue.current()
    if (!question) return
    this.queue.skip(question)
    this.nextQuestion()
  }

  // "Lieber eintippen" / "Andere … wählen": the field itself, the question set aside.
  typeAnswer() {
    if (this.advancing) return
    const question = this.queue.current()
    if (!question) return
    this.queue.skip(question)
    const input = namedInput(this.form(), this.answerFieldName(question) || "")
    const visual = input ? this.visualFor(input) || input : null
    this.finishQuestions()
    visual?.scrollIntoView?.({ block: "center" })
    visual?.focus?.()
  }

  setAdvancing(advancing) {
    this.advancing = advancing
    if (this.hasQuestionOptionsTarget) {
      this.questionOptionsTarget.querySelectorAll("button").forEach((button) => {
        button.disabled = advancing
      })
    }
  }

  setAnswering(question) {
    this.answering = question
    this.element.dataset.answering = question ? "true" : "false"
  }

  // The queue has moved on, but the next question shows only once the required
  // check returns: until then the card's buttons would act on a question the
  // user has not seen (a double tap answering it with the old one's option).
  async nextQuestion() {
    this.setAdvancing(true)
    try {
      await this.refreshRequired()
    } finally {
      this.setAdvancing(false)
    }
    if (this.queue.current()) {
      this.renderQuestion()
      this.setState("question")
    } else {
      this.finishQuestions()
    }
  }

  // The required fields the form leaves empty now (no AI call).
  async refreshRequired() {
    if (!this.questionsUrlValue) return
    const body = this.questionsRequest()
    if (!body) return
    try {
      const { ok, payload } = await this.post(this.questionsUrlValue, {
        ...body,
        skipped: JSON.stringify(this.queue.closedIds()),
      })
      if (ok && this.errorsOf(payload).length === 0) this.queue.setRequired(payload.questions)
    } catch (error) {
      console.warn("Voice questions failed:", error)
    }
  }

  renderQuestion() {
    const question = this.queue.current()
    const { index, total } = this.queue.position()
    if (this.hasQuestionCounterTarget) {
      this.questionCounterTarget.textContent = this.message("questionCounter")
        .replace("{i}", String(index))
        .replace("{n}", String(total))
    }
    if (this.hasQuestionRemainingTarget) {
      this.questionRemainingTarget.textContent = this.plural("questionsLeft", total - index + 1)
    }
    if (this.hasQuestionDetailTarget) this.questionDetailTarget.textContent = question.detail || ""
    if (this.hasQuestionPromptTarget) this.questionPromptTarget.textContent = question.prompt
    if (this.hasQuestionOptionsTarget && this.hasQuestionOptionTemplateTarget) {
      this.questionOptionsTarget.replaceChildren(
        ...(question.options || []).map((option, position) => {
          const button = this.questionOptionTemplateTarget.content.firstElementChild.cloneNode(true)
          button.dataset.index = String(position)
          button.dataset.best = String(Boolean(option.best))
          button.setAttribute(
            "aria-checked",
            String(Boolean(option.best && question.selected != null)),
          )
          button.querySelector("[data-option-label]").textContent = option.label
          return button
        }),
      )
    }
    if (this.hasQuestionOtherTarget) {
      this.questionOtherTarget.classList.toggle(
        "hidden",
        !(question.kind === "choose" && question.allow_other),
      )
    }
    if (this.hasQuestionTypeTarget) {
      this.questionTypeTarget.classList.toggle("hidden", question.kind !== "speak")
    }
  }

  // No question left: the applied state, as "Alles erfasst" once any was answered.
  finishQuestions() {
    const answered = this.queue.answered
    if (answered.length) {
      this.appliedTitleTarget.textContent = this.message("doneTitle").replace(
        "{n}",
        String(this.sessionApplied),
      )
      this.appliedSubtitleTarget.textContent = this.plural("doneSubtitle", answered.length)
    }
    if (this.hasAnsweredListTarget) {
      this.answeredListTarget.replaceChildren(
        ...answered.map(({ question, value }) => {
          const item = document.createElement("li")
          item.className =
            "flex justify-between gap-3 rounded-[10px] bg-gray-50 px-3 py-2.5 text-[13px]"
          const label = document.createElement("span")
          label.className = "text-gray-600"
          label.textContent = [question.field_label, question.row_label].filter(Boolean).join(" · ")
          const text = document.createElement("span")
          text.className = "font-semibold text-gray-900"
          text.textContent = value || this.answerValue(question)
          item.append(label, text)
          return item
        }),
      )
      this.answeredListTarget.classList.toggle("hidden", answered.length === 0)
    }
    this.renderHints()
    this.setState("applied")
  }

  // What a spoken answer put into its field, as the form shows it.
  answerValue(question) {
    const input = namedInput(this.form(), this.answerFieldName(question) || "")
    if (!input) return ""
    if (input.tagName === "SELECT") {
      return Array.from(input.selectedOptions)
        .filter((option) => option.value !== "")
        .map((option) => option.textContent.trim())
        .join(", ")
    }
    return input.value
  }

  finish() {
    if (this.mediaRecorder?.state === "recording") this.mediaRecorder.stop()
  }

  cancel() {
    this.cancelled = true
    this.finish()
  }

  toggleExamples() {
    if (!this.hasExamplesTarget) return
    const open = this.examplesTarget.classList.contains("hidden")
    this.examplesTarget.classList.toggle("hidden", !open)
    this.examplesTarget.classList.toggle("flex", open)
  }

  toggleDetails() {
    this.setDetails(this.element.dataset.details !== "open")
  }

  undo() {
    if (!this.undoState) return
    this.restoreSnapshot(this.undoState.snapshot)
    this.untagAll()
    this.queue.reset()
    this.transcripts = []
    this.sessionApplied = 0
    this.setAnswering(null)
    this.hasAppliedOnce = this.undoState.hadApplied
    this.undoState = null
    this.setState("idle")
    if (this.hasIdleSubtitleTarget) this.idleSubtitleTarget.textContent = this.message("undone")
  }

  // --- Recording ---

  async start() {
    this.cancelled = false
    this.setState("arming")
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      this.showError(this.message("micDenied"))
      return
    }

    this.stream = stream
    this.recordedChunks = []
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/mp4"
    this.mimeType = mimeType.split(";")[0]
    const recorder = new MediaRecorder(stream, { mimeType })
    this.mediaRecorder = recorder
    recorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) this.recordedChunks.push(event.data)
    })
    recorder.addEventListener("stop", () => this.recordingStopped(recorder))
    recorder.start()

    this.levels = Array(this.meterBars().length).fill(0)
    this.meter = new VoiceLevelMeter(stream, (level) => this.renderLevel(level))
    this.meter.start()
    this.startedAt = Date.now()
    this.renderClock(0)
    clearInterval(this.clock)
    // Bound to this recorder: a later recording is never stopped by an earlier clock.
    this.clock = setInterval(() => {
      const elapsed = (Date.now() - this.startedAt) / 1000
      this.renderClock(elapsed)
      if (elapsed >= this.maxSecondsValue && recorder.state === "recording") recorder.stop()
    }, 250)
    this.setState("recording")
  }

  recordingStopped(recorder) {
    if (recorder !== this.mediaRecorder) return
    this.stopRecordingResources()
    if (this.cancelled || !this.recordedChunks.length) {
      this.setAnswering(null)
      this.setState(this.restingState)
      return
    }
    this.upload()
  }

  stopRecordingResources() {
    clearInterval(this.clock)
    this.clock = null
    this.meter?.stop()
    this.meter = null
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null
  }

  renderClock(seconds) {
    const whole = Math.min(Math.floor(seconds), this.maxSecondsValue)
    if (this.hasTimerTarget) {
      this.timerTarget.textContent = `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`
    }
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${Math.min(100, (seconds / this.maxSecondsValue) * 100)}%`
    }
  }

  renderLevel(level) {
    this.levels.push(level)
    this.levels.shift()
    this.meterBars().forEach((bar, index) => {
      bar.style.height = `${Math.round(6 + this.levels[index] * 42)}px`
    })
  }

  meterBars() {
    return this.hasMeterTarget ? Array.from(this.meterTarget.children) : []
  }

  // --- Requests ---

  upload() {
    this.setState("processing")
    this.setStep(0)
    const extension = this.mimeType === "audio/mp4" ? "m4a" : "webm"
    const file = new File(this.recordedChunks, `voice-input-${Date.now()}.${extension}`, {
      type: this.mimeType,
    })
    new DirectUpload(file, "/rails/active_storage/direct_uploads").create((error, blob) => {
      if (error) {
        this.showError(this.message("uploadFailed"))
        return
      }
      this.setStep(1)
      this.transcribe(blob.signed_id)
    })
  }

  async transcribe(signedId) {
    try {
      const { ok, payload } = await this.post(this.transcribeUrlValue, {
        signed_id: signedId,
        ...this.transcribeParams(),
      })
      if (!ok || this.errorsOf(payload).length > 0) {
        this.showError(this.errorsOf(payload).join(" ") || this.message("uploadFailed"))
        return
      }
      this.renderTranscript(payload.transcript, [])
      this.setState("finalizing")
      this.setStep(2)
      await this.extract(payload.transcript_token, payload.transcript)
    } catch (error) {
      console.warn("Voice transcription failed:", error)
      this.showError(this.message("uploadFailed"))
    }
  }

  async extract(token, transcript) {
    const { body, correction } = this.extractRequest(token)
    if (this.answering) body.question = JSON.stringify(this.answering)
    if (this.transcripts.length) body.earlier = JSON.stringify(this.transcripts.slice(-3))
    const { ok, payload } = await this.post(this.extractUrlValue, body)
    if (!ok || this.errorsOf(payload).length > 0) {
      this.showError(this.errorsOf(payload).join(" ") || this.message("uploadFailed"))
      return
    }
    if (transcript) this.transcripts.push(transcript)
    this.renderTranscript(transcript, payload.highlights || [], payload.unmatched_highlights || [])
    // A beat on the marked transcript, so the user sees what was taken from it.
    await new Promise((resolve) => setTimeout(resolve, 700))
    await this.applyResult(payload, correction)
  }

  // Extra params of the transcribe step (the extract step's are extractRequest's).
  transcribeParams() {
    return {}
  }

  // Feature hooks of the Rückfragen (see the header); none: only what a pass asks.
  questionsRequest() {
    return null
  }

  applyAnswer() {
    return 0
  }

  answerFieldName() {
    return null
  }

  // `{ errors }` from the endpoints, `{ error }` from a refused (403) request.
  errorsOf(payload) {
    if (Array.isArray(payload.errors)) return payload.errors
    return payload.error ? [payload.error] : []
  }

  async post(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    })
    const payload = await response.json().catch(() => ({}))
    return { ok: response.ok, payload }
  }

  // --- Applying ---

  async applyResult(payload, correction) {
    const form = this.form()
    const answering = this.answering
    const before = fieldValues(form)
    const snapshot = this.takeSnapshot()
    // An answer adds to the pass it answers: its tags and its undo point stay.
    if (!answering) this.untagAll()

    const { applied, missing } = this.applyPayload(payload)
    const changed = changedFieldNames(before, fieldValues(form))
    const questions = payload.questions || []
    if (!answering) {
      this.undoState =
        applied > 0 || questions.length ? { snapshot, hadApplied: this.hasAppliedOnce } : null
      this.sessionApplied = applied
    } else {
      this.sessionApplied += applied
    }
    // Questions asked make the next recording a correction of what is on the form.
    this.hasAppliedOnce = this.hasAppliedOnce || applied > 0 || questions.length > 0
    changed.forEach((name) => this.tag(name))
    this.lastWarnings = payload.warnings || []
    this.lastMissing = missing
    // An answer that left its field empty (nothing usable heard) keeps the
    // question open instead of closing it as answered.
    const answered = Boolean(answering) && (applied > 0 || this.answerValue(answering) !== "")
    if (answered) this.queue.answer(answering, null)
    this.queue.addFromPass(questions, { answering, answered })
    this.setAnswering(null)
    this.renderApplied(applied, correction, this.hints(this.lastWarnings, missing), changed)

    await this.refreshRequired()
    if (this.queue.current()) {
      this.renderQuestion()
      this.setState("question")
    } else if (this.queue.answered.length) {
      this.finishQuestions()
    } else {
      this.setState("applied")
    }
  }

  // The hints of the last pass, minus warnings a Rückfrage asks about instead.
  renderHints() {
    const hints = this.hints(this.lastWarnings || [], this.lastMissing || [])
    this.hintCount = hints.length
    if (hints.length && !this.queue.answered.length) {
      this.appliedSubtitleTarget.textContent = this.plural("hints", hints.length)
    }
    this.hintListTarget.replaceChildren(
      ...hints.map((text) => {
        const item = document.createElement("li")
        item.textContent = text
        return item
      }),
    )
  }

  renderApplied(applied, correction, hints, changed) {
    const kind = correction ? "corrected" : "applied"
    this.appliedTitleTarget.textContent =
      applied === 0 ? this.message("noChanges") : this.plural(kind, applied)
    this.hintCount = hints.length
    this.appliedSubtitleTarget.textContent =
      hints.length > 0 ? this.plural("hints", hints.length) : this.message("appliedSubtitle")
    this.hintListTarget.replaceChildren(
      ...hints.map((text) => {
        const item = document.createElement("li")
        item.textContent = text
        return item
      }),
    )
    if (this.hasUndoButtonTarget) this.undoButtonTarget.disabled = !this.undoState
    this.renderSummary(changed)
  }

  hints(warnings, missing) {
    const covered = this.queue.coveredIds()
    const texts = warnings
      .filter((warning) => !warning.question_id || !covered.has(warning.question_id))
      .map((warning) => warning.message || warning.raw)
      .filter(Boolean)
    const keys = [...new Set(missing)]
    if (keys.length) {
      texts.push(`${this.message("notApplied")} ${keys.map((key) => this.label(key)).join(", ")}`)
    }
    return texts
  }

  chip(label, value) {
    if (!value || !this.hasChipTemplateTarget) return null
    const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true)
    chip.querySelector("[data-chip-label]").textContent = label
    chip.querySelector("[data-chip-value]").textContent = value
    return chip
  }

  label(key) {
    return this.labelsValue[key] || key
  }

  // Marks the phrases values were taken from (green) and, where they do not
  // overlap those, the phrases that filled nothing (amber).
  renderTranscript(text, phrases, unmatched = []) {
    const transcript = text || ""
    const lower = transcript.toLowerCase()
    const ranges = []
    const mark = (list, classes) =>
      list.forEach((phrase) => {
        const start = lower.indexOf(String(phrase).toLowerCase())
        if (start < 0) return
        const end = start + String(phrase).length
        if (ranges.some(([from, to]) => start < to && end > from)) return
        ranges.push([start, end, classes])
      })
    mark(phrases, HIGHLIGHT_CLASSES)
    mark(unmatched, UNMATCHED_CLASSES)
    ranges.sort((a, b) => a[0] - b[0])
    const nodes = []
    let cursor = 0
    ranges.forEach(([start, end, classes]) => {
      if (start > cursor) nodes.push(document.createTextNode(transcript.slice(cursor, start)))
      const mark = document.createElement("mark")
      mark.className = classes
      mark.textContent = transcript.slice(start, end)
      nodes.push(mark)
      cursor = end
    })
    if (cursor < transcript.length) nodes.push(document.createTextNode(transcript.slice(cursor)))
    this.transcriptTarget.replaceChildren(...nodes)
  }

  // --- "Sprache" tags ---

  tag(name) {
    const input = namedInput(this.form(), name)
    if (!input || (input.type === "hidden" && !input.closest("[data-controller]"))) return
    const visual = this.visualFor(input)
    if (!visual || this.tags.some((tag) => tag.visual === visual)) return
    visual.classList.add(...TAG_CLASSES)
    const label = this.labelFor(visual)
    let badge = null
    if (label && !label.querySelector("[data-voice-tag]")) {
      badge = document.createElement("span")
      badge.dataset.voiceTag = ""
      badge.className = BADGE_CLASSES
      badge.textContent = this.message("fieldTag")
      label.append(badge)
    }
    this.tags.push({ input, visual, badge })
  }

  untag(target) {
    this.tags = this.tags.filter((tag) => {
      const hit = tag.input === target || tag.visual === target || tag.visual.contains(target)
      if (hit) this.removeTag(tag)
      return !hit
    })
  }

  untagAll() {
    this.tags.forEach((tag) => this.removeTag(tag))
    this.tags = []
  }

  removeTag(tag) {
    tag.visual.classList.remove(...TAG_CLASSES)
    tag.badge?.remove()
  }

  // What the user sees for a named field: the select's SlimSelect box, a time
  // widget's display input, or the field itself.
  visualFor(input) {
    if (input.tagName === "SELECT") {
      const slim = input.parentElement?.querySelector(".ss-main")
      return slim || input
    }
    if (input.type === "hidden") {
      const widget = input.closest("[data-controller]")
      return widget?.querySelector("input:not([type=hidden]), select, button") || null
    }
    return input
  }

  labelFor(visual) {
    const field = visual.matches("input, select, textarea")
      ? visual
      : visual.parentElement?.querySelector("select")
    if (field?.id) {
      const label = this.form().querySelector(`label[for="${CSS.escape(field.id)}"]`)
      if (label) return label
    }
    return visual.closest("div")?.parentElement?.querySelector("label") || null
  }

  // --- State ---

  setState(state) {
    this.element.dataset.state = state
    if (!["recording", "processing", "finalizing", "arming"].includes(state) && this.answering) {
      this.setAnswering(null)
    }
    if (state !== "applied") this.setDetails(false)
    if (state !== "idle" && this.hasIdleSubtitleTarget)
      this.idleSubtitleTarget.textContent = this.idleText
    if (RESTING.includes(state)) this.restingState = state
    if (this.hasMicTarget) this.micTarget.disabled = WORKING.includes(state)
    const hints = state === "applied" && this.hintCount > 0
    this.hintsTarget.classList.toggle("hidden", !hints)
    this.hintsTarget.classList.toggle("flex", hints)
  }

  setStep(active) {
    this.stepTargets.forEach((step, index) => {
      step.dataset.stepState = index < active ? "done" : index === active ? "active" : "pending"
    })
  }

  setDetails(open) {
    this.element.dataset.details = open ? "open" : "closed"
    if (!this.hasDetailsToggleTarget) return
    this.detailsToggleTarget.setAttribute("aria-expanded", String(open))
    this.detailsToggleTarget.textContent = this.message(open ? "detailsHide" : "detailsShow")
  }

  showError(message) {
    this.stopRecordingResources()
    this.errorMessageTarget.textContent = message
    this.setState("error")
  }

  // --- Helpers ---

  message(key) {
    return this.messagesValue[key] || ""
  }

  plural(kind, count) {
    return count === 1
      ? this.message(`${kind}One`)
      : this.message(`${kind}Other`).replace("{n}", String(count))
  }

  form() {
    return this.element.closest("form") || this.element
  }
}
